import { useState, useEffect, useCallback } from 'react';
import { Ticket, Plus, Trash2, Edit2, Check, X, Percent, User, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function AdminPromoCodes() {
  const { token } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    restricted_to_client_id: '',
    duration_months: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPromoCodes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/promo-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPromoCodes(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchPromoCodes();
    fetchClients();
  }, [fetchPromoCodes, fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      code: formData.code,
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      valid_from: formData.valid_from || null,
      valid_until: formData.valid_until || null,
      restricted_to_client_id: formData.restricted_to_client_id || null,
      duration_months: formData.duration_months ? parseInt(formData.duration_months) : null
    };

    try {
      const url = editingPromo 
        ? `${API_URL}/api/promo-codes/${editingPromo.id}`
        : `${API_URL}/api/promo-codes`;
      
      const res = await fetch(url, {
        method: editingPromo ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingPromo ? 'Code promo mis à jour' : 'Code promo créé');
        setShowModal(false);
        resetForm();
        fetchPromoCodes();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/promo-codes/${promoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Code promo supprimé');
        fetchPromoCodes();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (promo) => {
    try {
      const res = await fetch(`${API_URL}/api/promo-codes/${promo.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !promo.is_active })
      });

      if (res.ok) {
        toast.success(promo.is_active ? 'Code désactivé' : 'Code activé');
        fetchPromoCodes();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value.toString(),
      max_uses: promo.max_uses?.toString() || '',
      valid_from: promo.valid_from ? promo.valid_from.split('T')[0] : '',
      valid_until: promo.valid_until ? promo.valid_until.split('T')[0] : '',
      restricted_to_client_id: promo.restricted_to_client_id || '',
      duration_months: promo.duration_months?.toString() || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPromo(null);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      valid_from: '',
      valid_until: '',
      restricted_to_client_id: '',
      duration_months: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Codes promo</h1>
          <p className="text-muted-foreground">Gérez vos codes promotionnels pour l'hébergement</p>
        </div>
        <Button onClick={openCreateModal} className="btn-primary">
          <Plus className="mr-2 h-4 w-4" />
          Nouveau code
        </Button>
      </div>

      {promoCodes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucun code promo</h3>
            <p className="text-muted-foreground mb-4">
              Créez votre premier code promo pour offrir des réductions.
            </p>
            <Button onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Créer un code
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Liste des codes promo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold">Code</th>
                    <th className="text-left py-3 px-4 font-semibold">Réduction</th>
                    <th className="text-left py-3 px-4 font-semibold">Restriction</th>
                    <th className="text-left py-3 px-4 font-semibold">Utilisations</th>
                    <th className="text-left py-3 px-4 font-semibold">Statut</th>
                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((promo) => (
                    <tr key={promo.id} className="border-b border-border last:border-0">
                      <td className="py-4 px-4">
                        <code className="bg-muted px-2 py-1 rounded font-mono font-bold">
                          {promo.code}
                        </code>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1">
                          {promo.discount_type === 'percentage' ? (
                            <>
                              <Percent className="h-4 w-4 text-primary" />
                              <span>{promo.discount_value}%</span>
                            </>
                          ) : (
                            <>
                              <span>{promo.discount_value}€</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {promo.restricted_to_client_id ? (
                          <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-amber-500" />
                            <span className="text-sm text-muted-foreground">
                              {promo.restricted_to_client_name || 'Client spécifique'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Tous</span>
                        )}
                        {promo.duration_months && (
                          <span className="text-xs text-muted-foreground block mt-1">
                            {promo.duration_months} mois
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-muted-foreground">
                          {promo.current_uses || 0}
                          {promo.max_uses ? ` / ${promo.max_uses}` : ' / ∞'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => handleToggleActive(promo)}
                          className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                            promo.is_active
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {promo.is_active ? 'Actif' : 'Inactif'}
                        </button>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(promo)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(promo.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5 text-primary" />
                {editingPromo ? 'Modifier le code' : 'Nouveau code promo'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Code *</label>
                  <Input
                    placeholder="Ex: WELCOME10"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    className="uppercase"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Type</label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      value={formData.discount_type}
                      onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    >
                      <option value="percentage">Pourcentage (%)</option>
                      <option value="fixed">Montant fixe (€)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Valeur *</label>
                    <Input
                      type="number"
                      placeholder={formData.discount_type === 'percentage' ? '10' : '20'}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Nombre max d'utilisations</label>
                  <Input
                    type="number"
                    placeholder="Laisser vide pour illimité"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    min="1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Valide à partir du</label>
                    <Input
                      type="date"
                      value={formData.valid_from}
                      onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Valide jusqu'au</label>
                    <Input
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Restreindre à un client
                  </label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={formData.restricted_to_client_id}
                    onChange={(e) => setFormData({ ...formData, restricted_to_client_id: e.target.value })}
                  >
                    <option value="">Tous les clients (public)</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.full_name || client.company_name || client.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Si sélectionné, seul ce client pourra utiliser ce code.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Durée de la réduction
                  </label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                  >
                    <option value="">Permanente</option>
                    <option value="1">1 mois</option>
                    <option value="3">3 mois</option>
                    <option value="6">6 mois</option>
                    <option value="12">1 an</option>
                    <option value="24">2 ans</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Après cette durée, l'abonnement repasse au tarif normal automatiquement.
                  </p>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" className="btn-primary">
                    {editingPromo ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
